/* =========================================================================
   Envelope Maker — assets/envelope-maker.js (bearly.art)
   Runs on the Bearly Tools shell. GA4: pushes em_* events to window.dataLayer
   and calls BT.track / BT.download; the shared core (assets/bearly-tools-core.js)
   sends them to GA4.
   Download popup + email gate: the ONE shared popup in the core (BT.runWithProgress):
   progress (this tool's stage texts per SVG / PDF), email step only if not unlocked,
   then the file. The core owns the popup, gate, Klaviyo subscribe, unlock keys and the
   em_email_* / em_progress_cancel / em_export_error events. Old core cached (no
   runWithProgress): the file is made straight away.
   Geometry + PDF engine unchanged from the standalone envelope-maker.html.

   2026-10-06 fix: seal flap on every cross style now guaranteed to overlap the
   back panel (Square Flap was ~0.1" short at every size, so the envelope never
   closed). Minimum overlap = max(0.3", 10% of envelope height).
   ========================================================================= */
(function () {
  "use strict";

  var root = document.getElementById("envelope-root");
  if (!root) return;

  /* ---------------- Core helpers (fall back quietly if the core failed to load) ---------------- */
  var emTrack = function (name, params) {
    var o = params ? JSON.parse(JSON.stringify(params)) : {};
    o.event = name;
    (window.dataLayer = window.dataLayer || []).push(o);
  };

  function saveBlob(data, type, name) {
    var blob = new Blob([data], { type: type });
    var url = URL.createObjectURL(blob);
    var a = document.createElement("a");
    a.href = url;
    a.download = (window.BT && BT.brandName ? BT.brandName(name) : name);
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(function () { URL.revokeObjectURL(url); }, 4000);
  }

  function btDownload(data, name, mime) {
    if (/svg/.test(mime) && window.BT && window.BT.trueSize) data = window.BT.trueSize(data); // 72 units/in for Design Space
    if (window.BT && typeof window.BT.download === "function") { window.BT.download(data, name, mime); return; }
    saveBlob(data, mime, name);
    emTrack("em_download_click", { file_name: name });
  }

  /* Klaviyo per-tool events: the core sends them for the known email (adds tool + tool_handle) */
  function emKlaviyoEvent(name, props) {
    if (window.BT && typeof window.BT.klaviyoEvent === "function") window.BT.klaviyoEvent(name, props);
  }

  /* ---------------- Constants ---------------- */
  var PRESETS = { a2: [5.5, 4.25], a6: [6, 4.25], a7: [7, 5], a9: [8.5, 5.5], sq5: [5, 5], sq6: [6, 6] };
  var STYLE_NAMES = { square: "Square Flap", baronial: "Baronial", euro: "Euro Flap", petal: "Petal Fold", coin: "Coin Pocket" };
  var FRAC = { "0.125": "1/8″", "0.1875": "3/16″", "0.25": "1/4″", "0.3125": "5/16″", "0.375": "3/8″" };
  var PAGES = { letter: [8.5, 11], a4: [8.27, 11.69] };

  var $ = function (id) { return document.getElementById(id); };
  var fmt = function (v) { return parseFloat(v.toFixed(3)).toString(); };
  var n2 = function (v) { return (Math.round(v * 100) / 100).toString(); };

  var ui = { foldMode: "dash", page: "letter" };

  function getState() {
    var style = root.querySelector("input[name=em-style]:checked").value;
    return {
      cardW: Math.min(12, Math.max(1, parseFloat($("em-cw").value) || 1)),
      cardH: Math.min(12, Math.max(1, parseFloat($("em-ch").value) || 1)),
      clear: parseFloat($("em-clr").value),
      style: style,
      foldMode: ui.foldMode,
      glue: style === "petal" ? false : $("em-glue").checked,
      radius: parseFloat($("em-rad").value)
    };
  }

  /* ---------- geometry (inches, y down; face rect = 0,0 .. W,H) ---------- */

  function crossGeom(style, W, H, s) {
    var sw = Math.min(0.38 * W, 1.75);
    var bh = 0.575 * H;
    var th = style === "square" ? 0.40 * H : style === "baronial" ? 0.60 * H : 0.87 * H;
    /* Seal flap must land on the back panel: back panel edge sits at (H - bh) from the
       top fold, so the flap needs th > H - bh. Enforce a minimum overlap. */
    var minOverlap = Math.max(0.3, 0.1 * H);
    th = Math.max(th, (H - bh) + minOverlap);
    var aT = Math.min(0.5 * sw, 0.9);
    var aB = Math.min(0.85 * sw, 1.4);
    var bi = Math.min(0.18 * W, 0.3 * W);
    var r = s.radius, rs = 0.1, rb = 0.12;
    var pts = [], rad = [];
    var P = function (x, y, rr) { pts.push([x, y]); rad.push(rr); };
    P(0, 0, 0);
    if (style === "square") {
      var ti = Math.min(0.12 * W, 0.6);
      P(ti, -th, r); P(W - ti, -th, r);
    } else {
      P(W / 2, -th, r * 1.4);
    }
    P(W, 0, 0);
    P(W + sw, aT, rs); P(W + sw, H - aB, rs); P(W, H, 0);
    P(W - bi, H + bh, rb); P(bi, H + bh, rb); P(0, H, 0);
    P(-sw, H - aB, rs); P(-sw, aT, rs);
    var folds = [[0, 0, W, 0], [W, 0, W, H], [0, H, W, H], [0, 0, 0, H]];
    var gw = Math.min(0.35, (W - 2 * bi) / 3);
    var glue = s.glue ? [
      [[W, H], [W - bi, H + bh], [W - bi - gw, H + bh], [W - gw, H]],
      [[0, H], [bi, H + bh], [bi + gw, H + bh], [gw, H]]
    ] : [];
    return { type: "poly", pts: pts, rad: rad, folds: folds, glue: glue, bounds: [-sw, -th, W + sw, H + bh] };
  }

  function petalGeom(W, H) {
    var pv = 0.65 * H, ph = 0.65 * W;
    return {
      type: "petal", W: W, H: H, pv: pv, ph: ph,
      folds: [[0, 0, W, 0], [W, 0, W, H], [0, H, W, H], [0, 0, 0, H]],
      glue: [],
      bounds: [-ph, -pv, W + ph, H + pv]
    };
  }

  function coinGeom(W, H, s) {
    var tf = Math.min(1.0, 0.3 * H);
    var tw = 0.5, gTop = 0.2, Hb = Math.max(1, H - 0.2);
    var r = s.radius, rb = 0.25, rt = 0.08;
    var tabs = s.glue;
    var pts = [], rad = [];
    var P = function (x, y, rr) { pts.push([x, y]); rad.push(rr); };
    P(0, 0, 0); P(0.35, -tf, r); P(W - 0.35, -tf, r); P(W, 0, 0);
    if (tabs) { P(W, gTop, 0); P(W + tw, gTop + 0.3, rt); P(W + tw, H - 0.15, rt); }
    P(W, H, 0);
    P(W, H + Hb, rb); P(0, H + Hb, rb);
    P(0, H, 0);
    if (tabs) { P(-tw, H - 0.15, rt); P(-tw, gTop + 0.3, rt); P(0, gTop, 0); }
    var folds = [[0, 0, W, 0], [0, H, W, H]];
    if (tabs) { folds.push([W, gTop, W, H], [0, gTop, 0, H]); }
    var glue = tabs ? [
      [[W, gTop], [W + tw, gTop + 0.3], [W + tw, H - 0.15], [W, H]],
      [[0, gTop], [-tw, gTop + 0.3], [-tw, H - 0.15], [0, H]]
    ] : [];
    return { type: "poly", pts: pts, rad: rad, folds: folds, glue: glue, bounds: [tabs ? -tw : 0, -tf, W + (tabs ? tw : 0), H + Hb] };
  }

  function buildGeometry(s) {
    var W = s.cardW + s.clear, H = s.cardH + s.clear;
    var g;
    if (s.style === "petal") g = petalGeom(W, H);
    else if (s.style === "coin") g = coinGeom(W, H, s);
    else g = crossGeom(s.style, W, H, s);
    g.W = W; g.H = H;
    return g;
  }

  /* ---------- outline as a command list (M/L/C, inches) shared by SVG + PDF ---------- */

  function polyCmds(pts, rad) {
    var n = pts.length, seg = [];
    for (var i = 0; i < n; i++) {
      var p = pts[i], r = rad[i] || 0;
      if (r < 0.005) { seg.push({ e1: p, v: null, e2: p }); continue; }
      var pr = pts[(i - 1 + n) % n], nx = pts[(i + 1) % n];
      var d1 = Math.hypot(pr[0] - p[0], pr[1] - p[1]), d2 = Math.hypot(nx[0] - p[0], nx[1] - p[1]);
      var d = Math.min(r, d1 / 2 - 0.01, d2 / 2 - 0.01);
      if (d <= 0.005) { seg.push({ e1: p, v: null, e2: p }); continue; }
      seg.push({
        e1: [p[0] + (pr[0] - p[0]) / d1 * d, p[1] + (pr[1] - p[1]) / d1 * d],
        v: p,
        e2: [p[0] + (nx[0] - p[0]) / d2 * d, p[1] + (nx[1] - p[1]) / d2 * d]
      });
    }
    var cmds = [["M", seg[0].e2[0], seg[0].e2[1]]];
    var corner = function (sgm) {
      var q = sgm.v, p0 = sgm.e1, p2 = sgm.e2;   /* quadratic -> cubic */
      cmds.push(["C",
        p0[0] + 2 / 3 * (q[0] - p0[0]), p0[1] + 2 / 3 * (q[1] - p0[1]),
        p2[0] + 2 / 3 * (q[0] - p2[0]), p2[1] + 2 / 3 * (q[1] - p2[1]),
        p2[0], p2[1]]);
    };
    for (var k = 1; k < n; k++) { var sg = seg[k]; cmds.push(["L", sg.e1[0], sg.e1[1]]); if (sg.v) corner(sg); }
    var s0 = seg[0]; cmds.push(["L", s0.e1[0], s0.e1[1]]); if (s0.v) corner(s0);
    return cmds;
  }

  function arcCmds(cx, cy, rx, ry, a0, a1, segs) {
    var out = [], step = (a1 - a0) / segs;
    for (var i = 0; i < segs; i++) {
      var t0 = a0 + i * step, t1 = t0 + step, k = 4 / 3 * Math.tan((t1 - t0) / 4);
      var p0 = [cx + rx * Math.cos(t0), cy + ry * Math.sin(t0)];
      var p3 = [cx + rx * Math.cos(t1), cy + ry * Math.sin(t1)];
      out.push(["C",
        p0[0] - k * rx * Math.sin(t0), p0[1] + k * ry * Math.cos(t0),
        p3[0] + k * rx * Math.sin(t1), p3[1] - k * ry * Math.cos(t1),
        p3[0], p3[1]]);
    }
    return out;
  }

  function petalCmds(g) {
    var W = g.W, H = g.H, pv = g.pv, ph = g.ph, PI = Math.PI;
    return [["M", 0, 0]].concat(
      arcCmds(W / 2, 0, W / 2, pv, PI, 2 * PI, 2),          /* top    */
      arcCmds(W, H / 2, ph, H / 2, 1.5 * PI, 2.5 * PI, 2),  /* right  */
      arcCmds(W / 2, H, W / 2, pv, 0, PI, 2),               /* bottom */
      arcCmds(0, H / 2, ph, H / 2, 0.5 * PI, 1.5 * PI, 2)   /* left   */
    );
  }

  var outlineCmds = function (g) { return g.type === "petal" ? petalCmds(g) : polyCmds(g.pts, g.rad); };

  /* gap-first dashes: dashes never touch fold endpoints */
  function dashSegs(x1, y1, x2, y2) {
    var L = Math.hypot(x2 - x1, y2 - y1);
    var dash = 0.15, gapMin = 0.09;
    var n = Math.max(1, Math.floor((L - gapMin) / (dash + gapMin)));
    while (n > 1 && n * dash >= L) n--;
    var g = (L - n * dash) / (n + 1);
    var ux = (x2 - x1) / L, uy = (y2 - y1) / L;
    var out = [], t = g;
    for (var i = 0; i < n; i++) { out.push([x1 + ux * t, y1 + uy * t, x1 + ux * (t + dash), y1 + uy * (t + dash)]); t += dash + g; }
    return out;
  }

  /* ---------- SVG (preview + export, px @ 96/in) ---------- */

  function coords(minX, minY, pad) {
    var ox = pad - minX, oy = pad - minY;
    return {
      X: function (x) { return +((x + ox) * 96).toFixed(2); },
      Y: function (y) { return +((y + oy) * 96).toFixed(2); },
      R: function (v) { return +(v * 96).toFixed(2); }
    };
  }

  function cmdsToD(cmds, c) {
    return cmds.map(function (k) {
      return k[0] === "M" ? "M " + c.X(k[1]) + " " + c.Y(k[2]) :
        k[0] === "L" ? "L " + c.X(k[1]) + " " + c.Y(k[2]) :
        "C " + c.X(k[1]) + " " + c.Y(k[2]) + " " + c.X(k[3]) + " " + c.Y(k[4]) + " " + c.X(k[5]) + " " + c.Y(k[6]);
    }).join(" ") + " Z";
  }

  /* Export keeps the original presentation colors; the preview uses classes so it follows the kit accent. */
  function foldMarkup(g, c, s, mode) {
    var exp = mode === "export";
    var lw = exp ? 1 : 2;
    var lines = "";
    if (s.foldMode === "dash") {
      var paint = exp ? 'stroke="#000000"' : 'class="em-dash"';
      g.folds.forEach(function (f) {
        dashSegs(f[0], f[1], f[2], f[3]).forEach(function (sg) {
          lines += '<line x1="' + c.X(sg[0]) + '" y1="' + c.Y(sg[1]) + '" x2="' + c.X(sg[2]) + '" y2="' + c.Y(sg[3]) + '" ' + paint + ' stroke-width="' + lw + '" stroke-linecap="round"/>';
        });
      });
      return '<g id="fold-lines">' + lines + "</g>";
    }
    var sPaint = exp ? 'stroke="#1e2d7d"' : 'class="em-score"';
    g.folds.forEach(function (f) {
      lines += '<line x1="' + c.X(f[0]) + '" y1="' + c.Y(f[1]) + '" x2="' + c.X(f[2]) + '" y2="' + c.Y(f[3]) + '" ' + sPaint + ' stroke-width="' + lw + '" stroke-linecap="round"/>';
    });
    return '<g id="score-layer">' + lines + "</g>";
  }

  function svgMarkup(g, s, mode) {
    var exp = mode === "export";
    var pad = exp ? 0.06 : 0.24;
    var mx = g.bounds[0], my = g.bounds[1], Mx = g.bounds[2], My = g.bounds[3];
    var bw = Mx - mx + 2 * pad, bh = My - my + 2 * pad;
    var c = coords(mx, my, pad);
    var cut = '<path d="' + cmdsToD(outlineCmds(g), c) + '" fill="none" ' +
      (exp ? 'stroke="#000000" stroke-width="1"' : 'class="em-cut" stroke-width="2.5"') + ' stroke-linejoin="round"/>';
    var folds = foldMarkup(g, c, s, mode);

    if (exp) {
      return '<svg xmlns="http://www.w3.org/2000/svg" width="' + fmt(bw) + 'in" height="' + fmt(bh) + 'in" viewBox="0 0 ' + fmt(bw * 96) + " " + fmt(bh * 96) + '">' + cut + folds + "</svg>";
    }

    var face = '<rect class="em-face" x="' + c.X(0) + '" y="' + c.Y(0) + '" width="' + c.R(g.W) + '" height="' + c.R(g.H) + '"/>';
    var glue = "";
    g.glue.forEach(function (q) {
      glue += '<polygon class="em-glue" points="' + q.map(function (p) { return c.X(p[0]) + "," + c.Y(p[1]); }).join(" ") + '"/>';
    });
    var fs = c.R(Math.min(g.W, g.H) * 0.09);
    var label = '<text class="em-label" x="' + c.X(g.W / 2) + '" y="' + c.Y(g.H / 2) + '" text-anchor="middle" dominant-baseline="middle" font-family="inherit" font-size="' + fs + '">' +
      fmt(s.cardW) + " × " + fmt(s.cardH) + "″ card</text>";
    return '<svg viewBox="0 0 ' + fmt(bw * 96) + " " + fmt(bh * 96) + '" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="' +
      STYLE_NAMES[s.style] + ' envelope template preview">' + face + glue + cut + folds + label + "</svg>";
  }

  /* ---------- PDF printout (hand-rolled, pt @ 72/in, true scale, tiled) ---------- */

  var PDF_M = 0.4, PDF_TH = 0.55, PDF_OV = 0.25;   /* margin, title band, tile overlap (in) */
  var NAVY = "0.118 0.176 0.49", GREY = "0.45 0.47 0.53";

  var pdfEsc = function (t) { return t.replace(/\\/g, "\\\\").replace(/\(/g, "\\(").replace(/\)/g, "\\)"); };

  function pdfContent(g, s, pw, ph, tile) {
    var availW = pw - 2 * PDF_M, availH = ph - 2 * PDF_M - PDF_TH;
    var mx = function (x) { return n2((PDF_M + (x - tile.tx0)) * 72); };
    var my = function (y) { return n2((ph - PDF_M - PDF_TH - (y - tile.ty0)) * 72); };
    var c = "";

    /* art title block */
    var line1 = "Bearly Art  -  Envelope Maker";
    var line2 = STYLE_NAMES[s.style] + "  -  " + fmt(s.cardW) + " x " + fmt(s.cardH) + " in card  -  flat " + n2(tile.bw) + " x " + n2(tile.bh) +
      " in  -  page " + tile.pn + " of " + tile.pt + "  -  print at 100% (actual size)";
    c += "BT /F1 12 Tf " + NAVY + " rg " + n2(PDF_M * 72) + " " + n2((ph - PDF_M - 0.2) * 72) + " Td (" + pdfEsc(line1) + ") Tj ET\n";
    c += "BT /F1 8 Tf " + GREY + " rg " + n2(PDF_M * 72) + " " + n2((ph - PDF_M - 0.38) * 72) + " Td (" + pdfEsc(line2) + ") Tj ET\n";
    c += GREY + " RG 0.5 w " + n2(PDF_M * 72) + " " + n2((ph - PDF_M - PDF_TH + 0.05) * 72) + " m " + n2((pw - PDF_M) * 72) + " " + n2((ph - PDF_M - PDF_TH + 0.05) * 72) + " l S\n";

    /* clipped template geometry */
    c += "q " + n2(PDF_M * 72) + " " + n2(PDF_M * 72) + " " + n2(availW * 72) + " " + n2(availH * 72) + " re W n\n";
    c += "0 0 0 RG 1 w 1 j 1 J\n";
    outlineCmds(g).forEach(function (k) {
      if (k[0] === "M") c += mx(k[1]) + " " + my(k[2]) + " m\n";
      else if (k[0] === "L") c += mx(k[1]) + " " + my(k[2]) + " l\n";
      else c += mx(k[1]) + " " + my(k[2]) + " " + mx(k[3]) + " " + my(k[4]) + " " + mx(k[5]) + " " + my(k[6]) + " c\n";
    });
    c += "h S\n";
    if (s.foldMode === "dash") {
      c += "0 0 0 RG 0.75 w\n";
      g.folds.forEach(function (f) {
        dashSegs(f[0], f[1], f[2], f[3]).forEach(function (sg) {
          c += mx(sg[0]) + " " + my(sg[1]) + " m " + mx(sg[2]) + " " + my(sg[3]) + " l S\n";
        });
      });
    } else {
      c += NAVY + " RG 0.75 w\n";
      g.folds.forEach(function (f) {
        c += mx(f[0]) + " " + my(f[1]) + " m " + mx(f[2]) + " " + my(f[3]) + " l S\n";
      });
    }
    c += "Q\n";

    /* tiling guides + footer */
    if (tile.cols > 1 || tile.rows > 1) {
      c += "[4 4] 0 d 0.65 0.65 0.65 RG 0.5 w\n";
      if (tile.i < tile.cols - 1) {
        var x = n2((PDF_M + availW - PDF_OV) * 72);
        c += x + " " + n2(PDF_M * 72) + " m " + x + " " + n2((PDF_M + availH) * 72) + " l S\n";
      }
      if (tile.j < tile.rows - 1) {
        var y = n2((PDF_M + PDF_OV) * 72);
        c += n2(PDF_M * 72) + " " + y + " m " + n2((PDF_M + availW) * 72) + " " + y + " l S\n";
      }
      c += "[] 0 d\n";
      var foot = "Tile row " + (tile.j + 1) + ", col " + (tile.i + 1) + "  -  pages overlap 0.25 in: match artwork at the dashed edges";
      c += "BT /F1 7 Tf " + GREY + " rg " + n2(PDF_M * 72) + " " + n2((PDF_M - 0.22) * 72) + " Td (" + pdfEsc(foot) + ") Tj ET\n";
    }
    return c;
  }

  function makePDF(pageContents, wPt, hPt) {
    var objs = [];
    var kids = pageContents.map(function (_, i) { return (4 + 2 * i) + " 0 R"; });
    objs[1] = "<< /Type /Catalog /Pages 2 0 R >>";
    objs[2] = "<< /Type /Pages /Kids [" + kids.join(" ") + "] /Count " + pageContents.length + " >>";
    objs[3] = "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>";
    pageContents.forEach(function (cs, i) {
      objs[4 + 2 * i] = "<< /Type /Page /Parent 2 0 R /MediaBox [0 0 " + wPt + " " + hPt + "] /Resources << /Font << /F1 3 0 R >> >> /Contents " + (5 + 2 * i) + " 0 R >>";
      objs[5 + 2 * i] = { stream: cs };
    });
    var out = "%PDF-1.4\n";
    var offs = [];
    for (var i = 1; i < objs.length; i++) {
      offs[i] = out.length;
      var o = objs[i];
      if (typeof o === "string") out += i + " 0 obj\n" + o + "\nendobj\n";
      else out += i + " 0 obj\n<< /Length " + o.stream.length + " >>\nstream\n" + o.stream + "\nendstream\nendobj\n";
    }
    var xref = out.length;
    out += "xref\n0 " + objs.length + "\n0000000000 65535 f \n";
    for (var j = 1; j < objs.length; j++) out += String(offs[j]).padStart(10, "0") + " 00000 n \n";
    out += "trailer\n<< /Size " + objs.length + " /Root 1 0 R >>\nstartxref\n" + xref + "\n%%EOF";
    return out;
  }

  function buildPDF(s, pageKey) {
    var g = buildGeometry(s);
    var pg = PAGES[pageKey] || PAGES.letter, pw = pg[0], ph = pg[1];
    var availW = pw - 2 * PDF_M, availH = ph - 2 * PDF_M - PDF_TH;
    var inset = 0.05;                                 /* keep edge strokes off the clip */
    var minX = g.bounds[0] - inset, minY = g.bounds[1] - inset;
    var bw = g.bounds[2] - g.bounds[0], bh = g.bounds[3] - g.bounds[1];
    var bwE = bw + 2 * inset, bhE = bh + 2 * inset;
    var cols = bwE <= availW ? 1 : Math.ceil((bwE - PDF_OV) / (availW - PDF_OV));
    var rows = bhE <= availH ? 1 : Math.ceil((bhE - PDF_OV) / (availH - PDF_OV));
    var pages = [];
    var pn = 1;
    for (var j = 0; j < rows; j++)
      for (var i = 0; i < cols; i++)
        pages.push(pdfContent(g, s, pw, ph, {
          tx0: minX + i * (availW - PDF_OV), ty0: minY + j * (availH - PDF_OV),
          i: i, j: j, cols: cols, rows: rows, bw: bw, bh: bh, pn: pn++, pt: cols * rows
        }));
    return { data: makePDF(pages, n2(pw * 72), n2(ph * 72)), pages: cols * rows };
  }

  /* ---------------- Render ---------------- */
  var view = $("em-view"), chip = $("em-chip"), boundsEl = $("em-bounds");
  var dlSvg = $("em-dlsvg"), dlPdf = $("em-dlpdf");

  function render() {
    var s = getState();
    var petal = s.style === "petal";
    $("em-glue").disabled = petal;
    $("em-glueRow").classList.toggle("em-off", petal);
    $("em-rad").disabled = petal;
    $("em-radRow").classList.toggle("em-off", petal);

    $("em-clrv").textContent = FRAC[String(s.clear)] || fmt(s.clear) + "″";
    $("em-radv").textContent = fmt(s.radius) + "″";

    var g = buildGeometry(s);
    view.innerHTML = svgMarkup(g, s, "preview");

    var bw = g.bounds[2] - g.bounds[0], bh = g.bounds[3] - g.bounds[1];
    var over = bw > 12.001 || bh > 12.001;
    boundsEl.textContent = "Flat template " + fmt(bw) + "″ × " + fmt(bh) + "″" + (over ? " — bigger than a 12″ mat" : "");
    chip.classList.toggle("is-bad", over);
  }

  /* ---------------- Engagement (fires once per page view) ---------------- */
  var customized = false;
  function noteChange(what) {
    if (customized) return;
    customized = true;
    var s = getState();
    emTrack("em_envelope_customized", { change: what, style: s.style, card: fmt(s.cardW) + "x" + fmt(s.cardH) });
    emKlaviyoEvent("Started Envelope Maker", { style: STYLE_NAMES[s.style], card_size: fmt(s.cardW) + "x" + fmt(s.cardH) });
  }

  /* ---------------- Downloads (shared popup: BT.runWithProgress in the core) ---------------- */
  var STAGES = {
    svg: [{ p: 22, wait: 300, text: "Measuring your card…" },
          { p: 55, wait: 700, text: "Folding the flaps…" },
          { p: 84, wait: 700, text: "Drawing cut and fold lines…" }],
    pdf: [{ p: 22, wait: 300, text: "Measuring your card…" },
          { p: 55, wait: 700, text: "Folding the flaps…" },
          { p: 84, wait: 700, text: "Laying out your pages…" }]
  };

  function setDownloads(on) { dlSvg.disabled = !on; dlPdf.disabled = !on; }

  function runWithProgress(kind, work) {
    if (document.querySelector(".bt-proc")) return;   // popup already open
    setDownloads(false);
    if (window.BT && BT.runWithProgress) {
      BT.runWithProgress({
        stages: STAGES[kind],
        gateText: "Your envelope is almost ready! Enter your email to get it.",
        doneText: "Your file is ready!",
        work: work,
        onEnd: function () { setDownloads(true); }
      });
      return;
    }
    // old core cached: no popup, just make the file
    try { work({ gated: false }); } catch (e) { if (window.console) console.error("[Envelope Maker]", e); }
    setDownloads(true);
  }

  function baseName(s) { return "Envelope " + STYLE_NAMES[s.style] + " " + fmt(s.cardW) + "x" + fmt(s.cardH) + " - BA"; }

  dlSvg.addEventListener("click", function () {
    var s = getState();
    runWithProgress("svg", function () {
      btDownload(svgMarkup(buildGeometry(s), s, "export"), baseName(s) + ".svg", "image/svg+xml");
      emKlaviyoEvent("Used Envelope Maker", { format: "svg", style: STYLE_NAMES[s.style], card_size: fmt(s.cardW) + "x" + fmt(s.cardH), fold_lines: s.foldMode });
    });
  });

  dlPdf.addEventListener("click", function () {
    var s = getState(), page = ui.page;
    runWithProgress("pdf", function () {
      var pdf = buildPDF(s, page);
      btDownload(pdf.data, baseName(s) + ".pdf", "application/pdf");
      emKlaviyoEvent("Used Envelope Maker", { format: "pdf", paper: page, pages: pdf.pages, style: STYLE_NAMES[s.style], card_size: fmt(s.cardW) + "x" + fmt(s.cardH), fold_lines: s.foldMode });
    });
  });

  /* ---------------- Wiring ---------------- */
  $("em-preset").addEventListener("change", function (e) {
    var p = PRESETS[e.target.value];
    if (p) { $("em-cw").value = p[0]; $("em-ch").value = p[1]; }
    render();
    noteChange("preset");
  });
  ["em-cw", "em-ch"].forEach(function (id) {
    $(id).addEventListener("input", function () { $("em-preset").value = "custom"; render(); noteChange("custom_size"); });
  });
  $("em-clr").addEventListener("input", function () { render(); noteChange("clearance"); });
  $("em-rad").addEventListener("input", function () { render(); noteChange("radius"); });
  $("em-glue").addEventListener("change", function () { render(); noteChange("glue"); });
  root.querySelectorAll("input[name=em-style]").forEach(function (r) {
    r.addEventListener("change", function () {
      render();
      emTrack("em_style_select", { style: r.value });
      noteChange("style");
    });
  });

  var foldHint = $("em-foldHint");
  var FOLD_HINTS = {
    dash: "Dashed folds cut on any machine — no scoring tool needed.",
    score: "Fold lines come in as a separate navy layer. Set it to Score in your cutting software."
  };
  $("em-fold").addEventListener("bt:change", function (e) {
    ui.foldMode = e.detail.value === "score" ? "score" : "dash";
    foldHint.textContent = FOLD_HINTS[ui.foldMode];
    render();
    noteChange("fold_lines");
  });
  $("em-page").addEventListener("bt:change", function (e) {
    ui.page = e.detail.value === "a4" ? "a4" : "letter";
  });

  // If the core never loaded, make the segmented controls work on their own.
  if (!window.BT) {
    root.querySelectorAll(".bt-seg").forEach(function (seg) {
      seg.addEventListener("click", function (e) {
        var b = e.target.closest("button");
        if (!b || !seg.contains(b)) return;
        seg.querySelectorAll("button").forEach(function (x) { x.setAttribute("aria-pressed", String(x === b)); });
        seg.dispatchEvent(new CustomEvent("bt:change", { detail: { value: b.value }, bubbles: true }));
      });
    });
  }

  render();
})();